import * as React from "react";
import { parseAgentOrigin } from "../../utils/agent-chat-utils";
import "../../styles/component/quick-agent-chat-panel/QuickAgentChatPanel.scss";

const DEFAULT_WIDTH = 450;
const MIN_WIDTH = 300;
const MAX_WIDTH_RATIO = 0.5;
const COLLAPSED_WIDTH = 48;

interface QuickAgentChatPanelProps {
    agentUrl: string;
    initialQuery?: string;
    defaultCollapsed?: boolean;
    autoCollapse?: boolean;
}

export const QuickAgentChatPanel: React.FC<QuickAgentChatPanelProps> = ({ agentUrl, initialQuery, defaultCollapsed = true, autoCollapse = false }) => {
    const [width, setWidth] = React.useState(DEFAULT_WIDTH);
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
    const [isResizing, setIsResizing] = React.useState(false);
    const isResizingRef = React.useRef(false);
    const previousWidth = React.useRef(DEFAULT_WIDTH);
    const listenersRef = React.useRef<{ move: (e: MouseEvent) => void; up: () => void } | null>(null);

    const agentOrigin = React.useMemo(() => parseAgentOrigin(agentUrl), [agentUrl]);

    // Clean up drag listeners on unmount
    React.useEffect(() => {
        return () => {
            if (listenersRef.current) {
                document.removeEventListener("mousemove", listenersRef.current.move);
                document.removeEventListener("mouseup", listenersRef.current.up);
            }
        };
    }, []);

    // Auto-collapse when navigating to search page (user can still manually expand)
    React.useEffect(() => {
        if (autoCollapse) {
            setCollapsed(true);
        }
    }, [autoCollapse]);

    // Sync the CSS variable with the current panel width
    React.useEffect(() => {
        if (!agentOrigin) {
            document.documentElement.style.removeProperty("--qa-panel-width");
            return;
        }
        const panelWidth = collapsed ? COLLAPSED_WIDTH : width;
        document.documentElement.style.setProperty("--qa-panel-width", `${panelWidth}px`);
        return () => {
            document.documentElement.style.removeProperty("--qa-panel-width");
        };
    }, [width, collapsed, agentOrigin]);

    if (!agentOrigin) {
        return null;
    }

    const iframeSrc = initialQuery
        ? `${agentUrl}?initialPrompt=${encodeURIComponent(initialQuery)}&showInitialPromptMessage=false`
        : agentUrl;

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        isResizingRef.current = true;
        setIsResizing(true);

        const onMouseMove = (e: MouseEvent) => {
            if (!isResizingRef.current) return;
            const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
            const newWidth = Math.min(Math.max(MIN_WIDTH, window.innerWidth - e.clientX), maxWidth);
            setWidth(newWidth);
        };

        const onMouseUp = () => {
            isResizingRef.current = false;
            setIsResizing(false);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            listenersRef.current = null;
        };

        listenersRef.current = { move: onMouseMove, up: onMouseUp };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const handleCollapse = () => {
        previousWidth.current = width;
        setCollapsed(true);
    };

    const handleExpand = () => {
        setCollapsed(false);
        setWidth(previousWidth.current || DEFAULT_WIDTH);
    };

    return (
        <>
            {/* Collapsed vertical tab - always in DOM, shown/hidden via display */}
            <div
                className="qa-chat-panel-collapsed"
                onClick={handleExpand}
                aria-label="Expand chat panel"
                style={{ display: collapsed ? undefined : "none" }}
            >
                <svg className="qa-collapsed-icon" viewBox="0 0 48 48" fill="none" width="28" height="28">
                    <path d="M24 6 C26 16, 32 22, 42 24 C32 26, 26 32, 24 42 C22 32, 16 26, 6 24 C16 22, 22 16, 24 6Z" fill="none" stroke="url(#qa-sparkle-gradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs><linearGradient id="qa-sparkle-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style={{stopColor: "#7c3aed"}}/><stop offset="100%" style={{stopColor: "#e84393"}}/></linearGradient></defs>
                </svg>
                <span className="qa-collapsed-label">PathFinder AI</span>
            </div>

            {/* Expanded panel - always in DOM, shown/hidden via display */}
            <div
                className={`qa-chat-panel${isResizing ? " qa-chat-panel--resizing" : ""}`}
                style={{ width, display: collapsed ? "none" : undefined }}
            >
                <div className="qa-resize-handle" onMouseDown={handleMouseDown} />
                <header className="qa-chat-panel-header">
                    <span className="qa-chat-panel-title">
                        <svg className="qa-chat-panel-title-icon" viewBox="0 0 48 48" fill="none" width="22" height="22">
                            <path d="M24 6 C26 16, 32 22, 42 24 C32 26, 26 32, 24 42 C22 32, 16 26, 6 24 C16 22, 22 16, 24 6Z" fill="none" stroke="url(#qa-sparkle-gradient-header)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            <defs><linearGradient id="qa-sparkle-gradient-header" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style={{stopColor: "#7c3aed"}}/><stop offset="100%" style={{stopColor: "#e84393"}}/></linearGradient></defs>
                        </svg>
                        PathFinder AI
                    </span>
                    <button className="qa-close-btn" onClick={handleCollapse} aria-label="Close chat panel">✕</button>
                </header>
                <iframe
                    key={iframeSrc}
                    className="qa-chat-iframe"
                    title="PathFinder AI Chat"
                    style={isResizing ? { pointerEvents: "none" } : undefined}
                    src={iframeSrc}
                    allow={`clipboard-read ${agentOrigin}; clipboard-write ${agentOrigin}`}
                    width="100%"
                    height="100%"
                />
            </div>
        </>
    );
};
